/**
 * Price calibration (COCKPIT-VISION.md build-order item 7, 2026-08-25
 * build; prices re-sourced from the tenant's own SP-API connection off
 * Keepa the same day — Faraz's decision) — the ops page that tells which
 * products can use optimal prices and which are underwater. React-query
 * hook for GET /api/price-calibration, plus every pure helper the page
 * needs (badge mapping, filter pills, search, sort, formatting) — unit
 * tested in price-calibration.test.ts (no component tests — codebase
 * convention). "Your price" (our own SP-API offer) is the verdict/margin
 * anchor; "Buy box" (Amazon's featured offer) is context only. Both refresh
 * automatically on the hourly Amazon sync — there is no more user-triggered
 * refresh (POST /refresh + its Keepa-budget UI removed).
 */
import { useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CompetitorRefreshResponse,
  PriceCalibrationResponse,
  type PriceCalibrationClearance,
  type PriceCalibrationCompetition,
  type PriceCalibrationRow,
  type PriceCalibrationVerdict,
} from '@sourcito/schemas';
import { apiFetch } from './api';
import { shortDateLabel } from './shipments';
import { moneyWhole, parseDollarInput, type MathCardInput } from './order-planning';
import {
  aggregateScenarioResults,
  computeRowScenario,
  feesCents as sharedFeesCents,
  selectedUnitsForScope,
  unitProfitCents as sharedUnitProfitCents,
  type InventoryBuckets,
  type InventoryScope,
  type RowPriceEconomics,
  type RowScenarioResult,
  type ScenarioAggregate,
  type ScenarioRowInput,
} from './profit-scenario';

// ─────────────────────────────────────────────────────────
// Query
// ─────────────────────────────────────────────────────────

export const PRICE_CALIBRATION_KEY = ['price-calibration'] as const;

export function usePriceCalibration(enabled = true) {
  return useQuery({
    queryKey: PRICE_CALIBRATION_KEY,
    enabled,
    queryFn: () => apiFetch('/api/price-calibration', { responseSchema: PriceCalibrationResponse }),
  });
}

// ─────────────────────────────────────────────────────────
// Competitor stock refresh (2026-10-05,
// docs/plans/2026-10-05-competitor-stock-clearance-design.md) — the button
// posts one batch at a time and the API says how many stale ASINs are left.
// The loop runs until none are left, or until the call cap below.
// ─────────────────────────────────────────────────────────

export const COMPETITOR_REFRESH_MAX_CALLS = 20;
export const COMPETITOR_REFRESH_NO_KEY_MESSAGE =
  'Add a Keepa key in Settings to refresh competitor stock.';
export const COMPETITOR_REFRESH_FAILED_MESSAGE = 'Could not refresh competitor stock. Try again.';

/** `error.code` from an ApiCallError's parsed body, or null. Duck-typed, the
 *  same way WarehousePrepPage reads it. */
function apiErrorCode(error: unknown): string | null {
  if (!(error instanceof Error) || !('details' in error)) return null;
  const details = error.details;
  return details && typeof details === 'object' && 'error' in details
    ? ((details.error as { code?: string } | null)?.code ?? null)
    : null;
}

/** Who may start a competitor stock refresh: the same owner/operator pair
 *  the API route enforces (it answers 403 to every other role). */
export function canRefreshCompetitorStock(role: string | null | undefined): boolean {
  return role === 'owner' || role === 'operator';
}

export interface CompetitorRefreshState {
  start: () => Promise<void>;
  isRunning: boolean;
  /** ASINs refreshed since `start()`. */
  refreshedSoFar: number;
  /** Stale ASINs left after the latest batch. Null until the first response. */
  remaining: number | null;
  error: string | null;
}

export function useRefreshCompetitorStock(): CompetitorRefreshState {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<Omit<CompetitorRefreshState, 'start'>>({
    isRunning: false,
    refreshedSoFar: 0,
    remaining: null,
    error: null,
  });
  const runningRef = useRef(false);

  const start = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setProgress({ isRunning: true, refreshedSoFar: 0, remaining: null, error: null });

    let batches = 0;
    let refreshed = 0;
    let error: string | null = null;
    try {
      while (batches < COMPETITOR_REFRESH_MAX_CALLS) {
        const batch = await apiFetch('/api/price-calibration/competitors/refresh', {
          method: 'POST',
          body: JSON.stringify({ force: true }),
          responseSchema: CompetitorRefreshResponse,
        });
        batches += 1;
        refreshed += batch.refreshed;
        setProgress((p) => ({ ...p, refreshedSoFar: refreshed, remaining: batch.remaining }));
        if (batch.remaining === 0) break;
      }
    } catch (e) {
      error =
        apiErrorCode(e) === 'CREDENTIAL_MISSING'
          ? COMPETITOR_REFRESH_NO_KEY_MESSAGE
          : COMPETITOR_REFRESH_FAILED_MESSAGE;
    }

    // Batches that landed changed the data even when a later one failed.
    if (batches > 0) await queryClient.invalidateQueries({ queryKey: PRICE_CALIBRATION_KEY });
    runningRef.current = false;
    setProgress((p) => ({ ...p, isRunning: false, error }));
  }, [queryClient]);

  return { start, ...progress };
}

// ─────────────────────────────────────────────────────────
// Formatting
// ─────────────────────────────────────────────────────────

export function money(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** currentMarginPct/tenants.min_margin_pct convention is a FRACTION (0-1),
 *  not 0-100 — this multiplies by 100 for display. Do not feed a 0-100
 *  value in here (that's order-planning.ts's `percent`, a different
 *  convention for a different page). */
export function marginPercent(fraction: number | null | undefined): string {
  if (fraction == null || !Number.isFinite(fraction)) return '—';
  return `${(fraction * 100).toFixed(1)}%`;
}

/** Store-settings fraction (0-1, tenants.min_margin_pct / target_margin_pct
 *  convention) -> a "N% margin" label for the Min/Target price column
 *  headers and the projection strip tiles (2026-08-25 build — those UI
 *  spots used to say just "Min price"/"At minimum prices" with no number,
 *  so nobody could tell what margin they were looking at). Round fractions
 *  render with no decimal ("10% margin"); non-round ones (e.g. a tenant-set
 *  0.125) keep exactly one decimal ("12.5% margin") — same one-decimal
 *  precision as marginPercent above, just phrased for a header instead of a
 *  table cell. Null/undefined/NaN (store settings not loaded yet) returns
 *  null so the caller falls back to the plain label rather than ever
 *  showing a stale or wrong hardcoded number. */
export function marginLabel(fraction: number | null | undefined): string | null {
  if (fraction == null || !Number.isFinite(fraction)) return null;
  const rounded = Math.round(fraction * 1000) / 10;
  const formatted = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${formatted}% margin`;
}

/** Bare numeric date (shared shortDateLabel format, e.g. "8-25-2026") under
 *  the Your price column, or a dash when the hourly sync hasn't priced this
 *  SKU yet. */
export function priceFreshnessLabel(priceCheckedAt: string | null): string {
  return priceCheckedAt ? shortDateLabel(priceCheckedAt) : '—';
}

/** Pre-first-sync banner predicate: true only when every row is missing
 *  Your price — i.e. this tenant's pricing leg hasn't run yet since deploy
 *  (a synced tenant lands a price within an hour, so this stays visible for
 *  at most one sync cycle). False for an empty list (module-off/empty-
 *  tenant state shows its own "no inventory" card, not this banner) and
 *  false the moment ANY row has a price (the per-row freshness dates +
 *  'No price' pills already communicate the partial state). */
export function noPricesFetched(rows: { yourPriceCents: number | null }[]): boolean {
  if (rows.length === 0) return false;
  return rows.every((r) => r.yourPriceCents === null);
}

/** Founder feedback (2026-09-03): "price calibration page is not showing
 *  the COGS" — the row showed profit per unit and margin% but never printed
 *  the landed cost itself (only inside the expanded math card). Plain cost
 *  line for every row, annotated with which cost basis costCents reflects.
 *  "Cost —" when there's no cost basis at all — never a bare dollar amount
 *  with no basis, and never a fallback amount that implies a basis. */
export function costLine(row: Pick<PriceCalibrationRow, 'costCents' | 'costBasis'>): string {
  if (row.costCents === null) return 'Cost —';
  const base = `Cost ${money(row.costCents)}`;
  if (row.costBasis === 'landed') return `${base} · landed`;
  if (row.costBasis === 'supplier_x_pack') return `${base} · supplier × pack`;
  return base;
}

// ─────────────────────────────────────────────────────────
// Buy box context — never used in verdict/margin math, just displayed
// alongside Your price with a you-hold-it marker and a subtle delta.
// ─────────────────────────────────────────────────────────

/** Buy Box minus Your price, in cents. Null when either side is missing —
 *  nothing to compare yet. */
export function buyboxDeltaCents(
  row: Pick<PriceCalibrationRow, 'yourPriceCents' | 'buyboxPriceCents'>
): number | null {
  if (row.yourPriceCents == null || row.buyboxPriceCents == null) return null;
  return row.buyboxPriceCents - row.yourPriceCents;
}

/** Signed, formatted delta for display (e.g. "+$1.00", "-$0.50"), "even"
 *  when they match exactly, or null when there's nothing to compare (caller
 *  renders nothing in that case rather than a dash — this is a bonus
 *  context line, not a required column value). */
export function buyboxDeltaLabel(
  row: Pick<PriceCalibrationRow, 'yourPriceCents' | 'buyboxPriceCents'>
): string | null {
  const delta = buyboxDeltaCents(row);
  if (delta === null) return null;
  if (delta === 0) return 'even';
  return `${delta > 0 ? '+' : '-'}${money(Math.abs(delta))}`;
}

// ─────────────────────────────────────────────────────────
// Verdict badge
// ─────────────────────────────────────────────────────────

type BadgeVariant = 'neutral' | 'accent' | 'positive' | 'warning' | 'negative' | 'info';

/** Extends order-planning.ts's VERDICT_BADGE mapping style (verdictBadge()
 *  there covers order-planning's 4-value verdict; this covers price
 *  calibration's 6-value verdict — a distinct enum, not shared). */
const VERDICT_BADGE: Record<PriceCalibrationVerdict, { variant: BadgeVariant; label: string }> = {
  underwater: { variant: 'negative', label: 'Underwater' },
  'below-floor': { variant: 'warning', label: 'Below floor' },
  'below-target': { variant: 'info', label: 'Below target' },
  optimal: { variant: 'positive', label: 'Optimal' },
  'no-price': { variant: 'neutral', label: 'No price yet' },
  'no-cost-basis': { variant: 'neutral', label: 'No cost basis' },
};

export function verdictBadge(verdict: PriceCalibrationVerdict): {
  variant: BadgeVariant;
  label: string;
} {
  return VERDICT_BADGE[verdict];
}

// ─────────────────────────────────────────────────────────
// Filter pills — All (default) · Underwater · Below floor · Below target ·
// Optimal · No price. 'no-cost-basis' has no dedicated pill (folds into
// "All" only) — same convention as order-planning.ts's 'unknown' bucket.
// ─────────────────────────────────────────────────────────

export type PriceCalibrationFilterKey =
  | 'all'
  | 'underwater'
  | 'below-floor'
  | 'below-target'
  | 'optimal'
  | 'no-price'
  | 'out-of-stock';

export const DEFAULT_PRICE_CALIBRATION_FILTER: PriceCalibrationFilterKey = 'all';

export const PRICE_CALIBRATION_FILTER_PILLS: { key: PriceCalibrationFilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'underwater', label: 'Underwater' },
  { key: 'below-floor', label: 'Below floor' },
  { key: 'below-target', label: 'Below target' },
  { key: 'optimal', label: 'Optimal' },
  { key: 'no-price', label: 'No price' },
  { key: 'out-of-stock', label: 'Out of stock' },
];

export function matchesPriceCalibrationFilter(
  row: { verdict: PriceCalibrationVerdict },
  filter: PriceCalibrationFilterKey
): boolean {
  if (filter === 'all') return true;
  return row.verdict === filter;
}

export function filterPriceCalibrationRows<T extends { verdict: PriceCalibrationVerdict }>(
  rows: T[],
  filter: PriceCalibrationFilterKey
): T[] {
  return rows.filter((r) => matchesPriceCalibrationFilter(r, filter));
}

/** Pure and total: every pill key present even for an empty list, so
 *  callers can render "Label · 0" without a fallback branch. Founder finding
 *  4 (2026-08-30 UAT): counts are scope-aware — a zero-stock-in-scope row
 *  counts toward "out-of-stock" ONLY, never toward a verdict pill or "all,"
 *  matching exactly what visiblePriceCalibrationRows shows for each pill (a
 *  count that doesn't match what clicking the pill reveals is worse than no
 *  count). */
export function priceCalibrationFilterCounts(
  rows: Pick<PriceCalibrationRow, 'verdict' | 'sellableNowUnits' | 'allOwnedUnits'>[],
  scope: InventoryScope
): Record<PriceCalibrationFilterKey, number> {
  const counts: Record<PriceCalibrationFilterKey, number> = {
    all: 0,
    underwater: 0,
    'below-floor': 0,
    'below-target': 0,
    optimal: 0,
    'no-price': 0,
    'out-of-stock': 0,
  };
  for (const r of rows) {
    if (isOutOfStockInScope(r, scope)) {
      counts['out-of-stock']++;
      continue;
    }
    counts.all++;
    switch (r.verdict) {
      case 'underwater':
        counts.underwater++;
        break;
      case 'below-floor':
        counts['below-floor']++;
        break;
      case 'below-target':
        counts['below-target']++;
        break;
      case 'optimal':
        counts.optimal++;
        break;
      case 'no-price':
        counts['no-price']++;
        break;
      case 'no-cost-basis':
        // No dedicated pill — only reflected in `all`.
        break;
    }
  }
  return counts;
}

// ─────────────────────────────────────────────────────────
// Search — over sku + name (reuses lib/amazon.ts's search conventions).
// ─────────────────────────────────────────────────────────

export function matchesPriceCalibrationSearch(
  row: Pick<PriceCalibrationRow, 'sellerSku' | 'productName'>,
  query: string
): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  return (
    row.sellerSku.toLowerCase().includes(trimmed) ||
    (row.productName?.toLowerCase().includes(trimmed) ?? false)
  );
}

export function searchPriceCalibrationRows<
  T extends Parameters<typeof matchesPriceCalibrationSearch>[0],
>(rows: T[], query: string): T[] {
  return rows.filter((r) => matchesPriceCalibrationSearch(r, query));
}

// ─────────────────────────────────────────────────────────
// Sort — default: margin ascending, nulls last (bleeders on top). Every
// numeric column sorts nulls-last regardless of direction (a row missing
// data reads as "unknown," not as an extreme value in either direction).
// ─────────────────────────────────────────────────────────

export type PriceCalibrationSortKey =
  | 'product'
  | 'cost'
  | 'minPrice'
  | 'targetPrice'
  | 'yourPrice'
  | 'margin';
export type PriceCalibrationSortDirection = 'asc' | 'desc';

function sortName(row: Pick<PriceCalibrationRow, 'sellerSku' | 'productName'>): string {
  return row.productName ?? row.sellerSku;
}

function compareNullsLast(
  a: number | null,
  b: number | null,
  direction: PriceCalibrationSortDirection
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const cmp = a - b;
  return direction === 'asc' ? cmp : -cmp;
}

export function comparePriceCalibrationRows(
  a: PriceCalibrationRow,
  b: PriceCalibrationRow,
  key: PriceCalibrationSortKey,
  direction: PriceCalibrationSortDirection
): number {
  switch (key) {
    case 'product': {
      const cmp = sortName(a).localeCompare(sortName(b));
      return direction === 'asc' ? cmp : -cmp;
    }
    case 'cost':
      return compareNullsLast(a.costCents, b.costCents, direction);
    case 'minPrice':
      return compareNullsLast(a.minPriceCents, b.minPriceCents, direction);
    case 'targetPrice':
      return compareNullsLast(a.targetPriceCents, b.targetPriceCents, direction);
    case 'yourPrice':
      return compareNullsLast(a.yourPriceCents, b.yourPriceCents, direction);
    case 'margin':
      return compareNullsLast(a.currentMarginPct, b.currentMarginPct, direction);
  }
}

export function sortPriceCalibrationRows(
  rows: PriceCalibrationRow[],
  key: PriceCalibrationSortKey,
  direction: PriceCalibrationSortDirection
): PriceCalibrationRow[] {
  return [...rows].sort((a, b) => comparePriceCalibrationRows(a, b, key, direction));
}

// ─────────────────────────────────────────────────────────
// Toolbar streamlining (Task B, docs/plans/2026-09-03-pricing-strategies-
// design.md §2) — the old three rows of scope tabs + verdict pills + six
// sort buttons collapse into a Filter dropdown and a Sort dropdown, each
// with a trigger button that must stay readable while its menu is closed
// ("Filter: Bleeders", "Sort: Margin ↑"). Same labels the menus themselves
// use, so trigger/menu/state-line chip never drift from each other.
// ─────────────────────────────────────────────────────────

/** Same labels the old SortButton row used, now shared by the Sort
 *  dropdown's radio items, its trigger, and the sort state chip. */
export const SORT_KEY_LABELS: Record<PriceCalibrationSortKey, string> = {
  margin: 'Margin now',
  product: 'Product',
  cost: 'Cost',
  yourPrice: 'Your price',
  minPrice: 'Min price',
  targetPrice: 'Target price',
};

/** "Sort: Margin now ↑" / "Sort: Your price ↓" — always shows the current
 *  sort, since a sort key/direction is always active (unlike the filter,
 *  there's no "unset" state to fall back to a bare "Sort"). */
export function sortTriggerLabel(
  sortKey: PriceCalibrationSortKey,
  direction: PriceCalibrationSortDirection
): string {
  const arrow = direction === 'asc' ? '↑' : '↓';
  return `Sort: ${SORT_KEY_LABELS[sortKey]} ${arrow}`;
}

/** The Filter dropdown merges "Inventory scope" and "Show" (verdict) into
 *  one menu/trigger — this surfaces whichever setting currently deviates
 *  from its default so the button reads "Filter" only when both are at
 *  their defaults. A "Show" filter (with its live count) takes priority
 *  over a non-default scope when both are set; the scope itself always has
 *  its own state-line chip regardless of what this trigger shows. */
export function filterTriggerLabel(
  scope: InventoryScope,
  filter: PriceCalibrationFilterKey,
  counts: Record<PriceCalibrationFilterKey, number>
): string {
  if (filter !== 'all') {
    const pill = PRICE_CALIBRATION_FILTER_PILLS.find((p) => p.key === filter);
    return `Filter: ${pill?.label ?? filter} · ${counts[filter]}`;
  }
  if (scope !== DEFAULT_INVENTORY_SCOPE) {
    const opt = INVENTORY_SCOPE_OPTIONS.find((o) => o.key === scope);
    return `Filter: ${opt?.label ?? scope}`;
  }
  return 'Filter';
}

// ─────────────────────────────────────────────────────────
// Sales projection — Faraz's ask, verbatim: "if we keep the current price
// and sell out all the items, how much money will we make in total? Same
// if we move everything to the minimum price, and to the maximum [target]
// price." Pure straight-line math over whatever row set is passed in (the
// caller feeds the FILTERED/searched rows — a projection over what's on
// screen, not always the whole catalog).
// ─────────────────────────────────────────────────────────

export type PriceCalibrationScenario = 'current' | 'minimum' | 'target';

export interface ScenarioProjection {
  revenueCents: number;
  profitCents: number;
  /** Rows with no price for THIS scenario — excluded from both revenue and
   *  profit (there's nothing to multiply units by). */
  excludedNoPriceCount: number;
  /** Rows that had a price for this scenario but no cost basis — counted
   *  toward revenue, excluded from profit only. */
  excludedNoCostCount: number;
}

export interface PriceCalibrationProjection {
  current: ScenarioProjection;
  minimum: ScenarioProjection;
  target: ScenarioProjection;
  /** Sum of unitsOnHand across every row, independent of scenario — the
   *  "selling through N units" context number. */
  unitsOnHandTotal: number;
}

function scenarioPriceCents(
  row: Pick<PriceCalibrationRow, 'yourPriceCents' | 'minPriceCents' | 'targetPriceCents'>,
  scenario: PriceCalibrationScenario
): number | null {
  switch (scenario) {
    case 'current':
      return row.yourPriceCents;
    case 'minimum':
      return row.minPriceCents;
    case 'target':
      return row.targetPriceCents;
  }
}

/** Fees at an arbitrary price, reconstructed client-side EXACTLY as the
 *  server's computeFeesCents would compute them for that price — see
 *  lib/price-calibration/calc.ts's feeComponents doc + cross-check test.
 *  Null when the row has no cost basis (no referralPct/fixedFeeCents).
 *  Delegates to profit-scenario.ts's feesCents (Task 1, 2026-08-26) — the
 *  shared kernel both this simulator and the scenario map use, so the two
 *  can never drift. */
export function feesAtPriceCents(
  row: Pick<PriceCalibrationRow, 'referralPct' | 'fixedFeeCents'>,
  priceCents: number
): number | null {
  return sharedFeesCents(row, priceCents);
}

function projectScenario(
  rows: PriceCalibrationRow[],
  scenario: PriceCalibrationScenario
): ScenarioProjection {
  let revenueCents = 0;
  let profitCents = 0;
  let excludedNoPriceCount = 0;
  let excludedNoCostCount = 0;

  for (const row of rows) {
    const priceCents = scenarioPriceCents(row, scenario);
    if (priceCents === null) {
      excludedNoPriceCount++;
      continue;
    }
    revenueCents += row.unitsOnHand * priceCents;

    if (row.costCents === null) {
      excludedNoCostCount++;
      continue;
    }
    const fees = feesAtPriceCents(row, priceCents);
    if (fees === null) {
      excludedNoCostCount++;
      continue;
    }
    // fees and row.costCents are both confirmed non-null above, so this is
    // never null — the shared signature stays nullable for other callers
    // that don't have that guarantee.
    const unitProfit = sharedUnitProfitCents(priceCents, fees, row.costCents) as number;
    profitCents += row.unitsOnHand * unitProfit;
  }

  return { revenueCents, profitCents, excludedNoPriceCount, excludedNoCostCount };
}

/** For the currently filtered row set: projected revenue/profit at the
 *  current, minimum, and target price for every row, straight-lined over
 *  today's units on hand (incl. inbound). Not a forecast — see the UI's
 *  caption. */
export function projectPriceCalibration(rows: PriceCalibrationRow[]): PriceCalibrationProjection {
  return {
    current: projectScenario(rows, 'current'),
    minimum: projectScenario(rows, 'minimum'),
    target: projectScenario(rows, 'target'),
    unitsOnHandTotal: rows.reduce((sum, r) => sum + r.unitsOnHand, 0),
  };
}

/** Shared context sentence under the projection strip. Exclusion counts
 *  here are about DATA COMPLETENESS (missing current price / missing cost),
 *  not scenario-specific — deliberately simpler than the per-scenario
 *  exclusion counts inside projectPriceCalibration. */
export function projectionContextLine(rows: PriceCalibrationRow[]): string {
  const unitsOnHandTotal = rows.reduce((sum, r) => sum + r.unitsOnHand, 0);
  const missingPrice = rows.filter((r) => r.yourPriceCents === null).length;
  const missingCost = rows.filter((r) => r.costCents === null).length;

  const parts = [
    `Selling through ${unitsOnHandTotal.toLocaleString('en-US')} units on hand (incl. inbound)`,
  ];
  if (missingPrice > 0) {
    parts.push(`excludes ${missingPrice} product${missingPrice === 1 ? '' : 's'} without a price`);
  }
  if (missingCost > 0) {
    parts.push(`${missingCost} without cost`);
  }
  return parts.join(' · ');
}

// ─────────────────────────────────────────────────────────
// Fees provenance (pricing-truth arc Task C, 2026-08-25) — small muted tag
// next to Margin now ("Amazon" vs "estimated") plus the bands' (Min/Target
// price) header note when every band-bearing row in view is Amazon-sourced.
// Mirrors order-planning.ts's feesSourceLabel in spirit but calibration's
// tag is compact (column-width), not the planner's full sentence.
// ─────────────────────────────────────────────────────────

export function feesSourceTag(feesSource: 'amazon' | 'estimated'): string {
  return feesSource === 'amazon' ? 'Amazon' : 'estimated';
}

/** Full sentence for the tag's tooltip / the bands' header note — only
 *  Amazon-sourced fees earn an explanatory note (the estimated fallback
 *  needs no extra justification; it already reads as "not the real thing"). */
export function feesSourceNote(feesSource: 'amazon' | 'estimated'): string | null {
  return feesSource === 'amazon' ? "from Amazon's fee rates for this item" : null;
}

/** Bands' column-header note — shown only when EVERY row that actually has
 *  a band (excludes no-price/no-cost-basis, which have no min/target price
 *  at all) is Amazon-sourced. A mixed row set says nothing rather than
 *  overclaiming accuracy for rows still on the estimated fallback. */
export function bandsFeesNote(
  rows: Pick<PriceCalibrationRow, 'verdict' | 'feesSource'>[]
): string | null {
  const withBands = rows.filter((r) => r.verdict !== 'no-price' && r.verdict !== 'no-cost-basis');
  if (withBands.length === 0) return null;
  return withBands.every((r) => r.feesSource === 'amazon')
    ? "from Amazon's fee rates for this item"
    : null;
}

// ─────────────────────────────────────────────────────────
// Buy-box context line — the prose version of buyboxDeltaLabel's compact
// table-cell delta, for the math card's buyBoxLine slot (Task C item 2):
// "buy box $18.49 (not yours) — you're $1.50 above".
// ─────────────────────────────────────────────────────────

export function buyBoxContextLine(
  row: Pick<PriceCalibrationRow, 'buyboxPriceCents' | 'buyboxIsYours' | 'yourPriceCents'>
): string | null {
  if (row.buyboxPriceCents === null) return null;
  const priceLabel = money(row.buyboxPriceCents);
  if (row.buyboxIsYours === true) return `buy box ${priceLabel} (yours)`;
  if (row.buyboxIsYours === false) {
    if (row.yourPriceCents === null) return `buy box ${priceLabel} (not yours)`;
    const deltaCents = row.yourPriceCents - row.buyboxPriceCents;
    if (deltaCents === 0) return `buy box ${priceLabel} (not yours) — you're even`;
    const direction = deltaCents > 0 ? 'above' : 'below';
    return `buy box ${priceLabel} (not yours) — you're ${money(Math.abs(deltaCents))} ${direction}`;
  }
  // buyboxIsYours === null — a Buy Box exists but ownership was never
  // resolved (mirrors the row's own display: no "yours"/delta either).
  return `buy box ${priceLabel}`;
}

// ─────────────────────────────────────────────────────────
// Shared math card adapter (Task C item 2) — PriceCalibrationRow ->
// MathCardInput, the same shape order-planning.ts's buildMathCard consumes
// (component reused verbatim, per the arc spec's "shared component" rule).
// null when there's nothing to build a card from yet — 'no-price'/
// 'no-cost-basis' verdicts — same "no expand affordance" convention as
// planLineToMathCardInput.
// ─────────────────────────────────────────────────────────

export function priceCalibrationRowToMathCardInput(
  row: PriceCalibrationRow,
  thresholds: { minFrac: number; targetFrac: number } | null
): MathCardInput | null {
  if (row.verdict === 'no-price' || row.verdict === 'no-cost-basis') return null;
  if (row.yourPriceCents === null || row.priceCheckedAt === null) return null;

  return {
    anchorPriceCents: row.yourPriceCents,
    // Calibration has no "intended price" concept — Your price (the
    // hourly-synced SP-API offer) is always the anchor.
    anchorSource: 'listed',
    feesCents: row.feesAtCurrentPriceCents,
    feesSource: row.feesSource,
    costCents: row.costCents,
    // No PO/invoice provenance on this row (unlike order_plan_lines) — the
    // shared card uses costBasis/unitsPerListing instead (multipack fix,
    // 2026-08-31), which outranks the invoice-based annotation.
    costSource: null,
    costBasis: row.costBasis,
    // R2 item 3 (D4): the ONE kernel grade vocabulary's source for the cost
    // above — 'lot_landed' renders as a settlement-grade annotation on the
    // math card (order-planning.ts's mathCardCostAnnotation).
    costBasisSource: row.costBasisSource,
    unitsPerListing: row.unitsPerListing,
    marginCents: row.currentMarginCents,
    marginFrac: row.currentMarginPct,
    thresholds,
    trendWarning: false,
    trend: null,
    verdictTier: row.verdict,
    checkedAt: row.priceCheckedAt,
    note: null,
    buyBoxLine: buyBoxContextLine(row),
    // Card line only when NOT 'authorized' (addendum) — buildMathCard's
    // authorizationCardLine handles the suppression.
    authorization: row.authorization,
  };
}

// ─────────────────────────────────────────────────────────
// Competition summary (Task C item 3) — client-side mirror of the API's
// lib/price-calibration/calc.ts#competitionSummary (verbatim port —
// gapToBuyBoxCents only when buyboxIsYours is STRICTLY false, headroomCents
// only when STRICTLY true; null stays "no data" on both, never treated as
// false).
// ─────────────────────────────────────────────────────────

export interface CompetitionSummaryInput {
  buyboxPriceCents: number | null;
  buyboxIsYours: boolean | null;
  yourPriceCents: number | null;
  lowestCompetitorCents: number | null;
  offersWithin5Pct: number | null;
}

export interface CompetitionSummary {
  holdsBuyBox: boolean | null;
  gapToBuyBoxCents: number | null;
  headroomCents: number | null;
  crowdedness: number | null;
}

export function competitionSummary(input: CompetitionSummaryInput): CompetitionSummary {
  const {
    buyboxPriceCents,
    buyboxIsYours,
    yourPriceCents,
    lowestCompetitorCents,
    offersWithin5Pct,
  } = input;

  let gapToBuyBoxCents: number | null = null;
  let headroomCents: number | null = null;

  if (buyboxIsYours === false && buyboxPriceCents !== null && yourPriceCents !== null) {
    gapToBuyBoxCents = buyboxPriceCents - yourPriceCents;
  } else if (buyboxIsYours === true && lowestCompetitorCents !== null && yourPriceCents !== null) {
    headroomCents = lowestCompetitorCents - yourPriceCents;
  }

  return {
    holdsBuyBox: buyboxIsYours,
    gapToBuyBoxCents,
    headroomCents,
    crowdedness: offersWithin5Pct,
  };
}

/** Signed dollar string with an explicit "+" for zero/positive — money()
 *  alone never prefixes a plus sign, and "headroom +$2.30" needs the sign to
 *  read as a direction, not just a quantity. */
export function signedMoney(cents: number | null): string {
  if (cents === null || !Number.isFinite(cents)) return '—';
  return cents < 0 ? `-${money(Math.abs(cents))}` : `+${money(cents)}`;
}

/** Same segments as competitionSummaryLabel, unjoined — the row renders each
 *  as its own non-wrapping line (founder feedback, 2026-09-03: the joined
 *  " · "-separated string wrapped mid-phrase in the narrow column, e.g. "BB
 *  $11.49 ·" / "match: -$3.50 · 1" / "near" across three ugly lines).
 *  competitionSummaryLabel below is a thin join() over this, so the two can
 *  never drift in wording. "—" (as a single-element array) for the no-data
 *  case — same convention as the label. */
export function competitionSummaryLines(
  row: Pick<
    PriceCalibrationRow,
    | 'buyboxPriceCents'
    | 'buyboxIsYours'
    | 'yourPriceCents'
    | 'lowestCompetitorCents'
    | 'offersWithin5Pct'
    | 'offerCount'
  >
): string[] {
  const summary = competitionSummary(row);

  if (summary.holdsBuyBox === true) {
    const parts: string[] = [];
    if (summary.headroomCents !== null)
      parts.push(`headroom ${signedMoney(summary.headroomCents)}`);
    if (row.offerCount !== null) {
      parts.push(`${row.offerCount} offer${row.offerCount === 1 ? '' : 's'}`);
    }
    return parts.length > 0 ? parts : ['—'];
  }

  if (summary.holdsBuyBox === false) {
    const parts: string[] = [];
    if (row.buyboxPriceCents !== null) parts.push(`BB ${money(row.buyboxPriceCents)}`);
    if (summary.gapToBuyBoxCents !== null) parts.push(`match: ${money(summary.gapToBuyBoxCents)}`);
    if (summary.crowdedness !== null) parts.push(`${summary.crowdedness} near`);
    return parts.length > 0 ? parts : ['—'];
  }

  return ['—'];
}

/** Compact Competition column render, per the arc spec's exact examples:
 *  not holding -> "BB $18.49 · match: −$1.50 · 3 near" (match = signed gap
 *  to the buy box, "near" = crowdedness i.e. offers within 5%); holding ->
 *  "headroom +$2.30 · 5 offers" (offers = raw offerCount, a competitive-
 *  pressure gauge — deliberately a DIFFERENT count than "near"); no data ->
 *  "—" (freshness date rendered separately by the caller, see
 *  priceFreshnessLabel(row.offersCheckedAt)). Delegates to
 *  competitionSummaryLines so the joined string and the per-segment render
 *  can never drift in wording. */
export function competitionSummaryLabel(
  row: Pick<
    PriceCalibrationRow,
    | 'buyboxPriceCents'
    | 'buyboxIsYours'
    | 'yourPriceCents'
    | 'lowestCompetitorCents'
    | 'offersWithin5Pct'
    | 'offerCount'
  >
): string {
  return competitionSummaryLines(row).join(' · ');
}

// ─────────────────────────────────────────────────────────
// Sales rank (docs/plans/2026-09-03-sales-rank-on-calibration.md) —
// demand signal shown next to velocity. Feeds NO calculation (margin,
// verdict, scenario, runway are untouched). Lower rank number = better:
// a rank that went DOWN since the comparison point is an improvement.
// ─────────────────────────────────────────────────────────

export interface SalesRankSegment {
  text: string;
  tone: 'positive' | 'negative' | 'neutral' | null;
}

export interface SalesRankSegments {
  head: string;
  vs7d: SalesRankSegment;
  vs30d: SalesRankSegment;
}

/** One "vs N ago" segment: "— vs 7d ago" with no comparison yet (tone
 *  null), "= vs 7d ago" when unchanged (tone neutral), else a directional
 *  arrow + the absolute rank-count change, toned positive (rank went DOWN
 *  — an improvement) or negative (rank went UP — worse). Rank direction is
 *  the opposite of a plain numeric comparison, so this is intentionally
 *  not shared with signedMoney's convention. */
function rankDeltaSegment(
  rank: number,
  comparisonRank: number | null,
  label: string
): SalesRankSegment {
  if (comparisonRank === null) return { text: `— ${label}`, tone: null };
  const improvement = comparisonRank - rank;
  if (improvement === 0) return { text: `= ${label}`, tone: 'neutral' };
  const tone = improvement > 0 ? 'positive' : 'negative';
  const arrow = improvement > 0 ? '▲' : '▼';
  return { text: `${arrow} ${Math.abs(improvement).toLocaleString('en-US')} ${label}`, tone };
}

/** Single place that computes the rank line's three parts (head + two
 *  toned "vs N ago" segments) so each arrow can be tinted independently —
 *  the row must NOT apply one tone to the whole line, since the 7d and 30d
 *  deltas can point opposite directions. Null when this SKU's ASIN has no
 *  observed rank yet. */
export function salesRankSegments(
  row: Pick<PriceCalibrationRow, 'salesRank'>
): SalesRankSegments | null {
  const salesRank = row.salesRank;
  if (!salesRank) return null;
  return {
    head: `#${salesRank.rank.toLocaleString('en-US')} in ${salesRank.title}`,
    vs7d: rankDeltaSegment(salesRank.rank, salesRank.rank7dAgo, 'vs 7d ago'),
    vs30d: rankDeltaSegment(salesRank.rank, salesRank.rank30dAgo, 'vs 30d ago'),
  };
}

/** "#12,034 in Grocery & Gourmet Food · ▲ 210 vs 7d ago · ▼ 1,400 vs 30d
 *  ago" — null (line absent entirely) when this SKU's ASIN has no observed
 *  rank yet. Thin composition of salesRankSegments for callers (tests,
 *  plain-text contexts) that just want the flattened string. */
export function salesRankLine(row: Pick<PriceCalibrationRow, 'salesRank'>): string | null {
  const segments = salesRankSegments(row);
  if (!segments) return null;
  return `${segments.head} · ${segments.vs7d.text} · ${segments.vs30d.text}`;
}

// ─────────────────────────────────────────────────────────
// Buy-box row highlight (Faraz, item 4) — subtle positive wash on rows
// where we hold the Buy Box, layered on top of the existing "yours" marker.
// ─────────────────────────────────────────────────────────

export function isBuyboxRow(row: Pick<PriceCalibrationRow, 'buyboxIsYours'>): boolean {
  return row.buyboxIsYours === true;
}

// ─────────────────────────────────────────────────────────
// Price simulator (Task C item 5, Faraz's "play button") — client-side
// what-if pricing. Every number recomputes from the row's REAL fee
// components (referralPct/fixedFeeCents) and the tenant's threshold
// fractions, mirroring the server's rounding/verdict-boundary rules exactly
// (Math.round(referralPct*price + fixedFeeCents); strict "<" boundaries) so
// a simulated number never drifts from what a real re-price would land on.
// NEVER written back to the row/server — a local "what if" only; callers
// must keep simulated state out of anything persisted.
// ─────────────────────────────────────────────────────────

export const SIMULATOR_STEP_CENTS = 25;

/** One $0.25 step, clamped at zero (a simulated price can't go negative). */
export function stepSimulatedPriceCents(
  currentCents: number,
  direction: 1 | -1,
  stepCents: number = SIMULATOR_STEP_CENTS
): number {
  return Math.max(0, currentCents + direction * stepCents);
}

/** Dollar-text -> cents for the simulator's editable field. Reuses
 *  order-planning.ts's parseDollarInput (a page-agnostic dollar parser) but
 *  rejects the empty-string/"clear the field" case — a simulated price is
 *  always a specific number; use reset to clear it instead. */
export function parseSimulatedPriceInput(text: string): number | null {
  const result = parseDollarInput(text);
  if (!result.ok || result.cents === null) return null;
  return result.cents;
}

/** Verdict-tier boundary rule, mirrored bit-for-bit from the server's calc:
 *  underwater at marginCents <= 0; strict "<" against minFrac/targetFrac so
 *  landing EXACTLY on a threshold reads as having cleared it (below-floor
 *  only for margin strictly under the floor; optimal at-or-above target). */
export function verdictTierForMargin(
  marginCents: number,
  marginFrac: number,
  thresholds: { minFrac: number; targetFrac: number }
): 'underwater' | 'below-floor' | 'below-target' | 'optimal' {
  if (marginCents <= 0) return 'underwater';
  if (marginFrac < thresholds.minFrac) return 'below-floor';
  if (marginFrac < thresholds.targetFrac) return 'below-target';
  return 'optimal';
}

export interface PriceCrossing {
  key: 'floor' | 'buybox' | 'lowestCompetitor';
  label: string;
}

/** Position markers: which threshold PRICES (not margin tiers) the
 *  simulated price newly crosses relative to where the LIVE price sits.
 *  "Newly" — a row already priced below its floor doesn't re-announce
 *  "crosses below your floor" just for staying there; it fires only on the
 *  side flip. Order is fixed (floor, buybox, lowestCompetitor) regardless of
 *  which actually fired, so the UI list doesn't reflow across renders. */
export function priceCrossings(
  row: Pick<
    PriceCalibrationRow,
    'yourPriceCents' | 'minPriceCents' | 'buyboxPriceCents' | 'lowestCompetitorCents'
  >,
  simulatedCents: number
): PriceCrossing[] {
  const live = row.yourPriceCents;
  const crossings: PriceCrossing[] = [];

  if (row.minPriceCents !== null) {
    const wasAtOrAboveFloor = live === null || live >= row.minPriceCents;
    if (wasAtOrAboveFloor && simulatedCents < row.minPriceCents) {
      crossings.push({ key: 'floor', label: 'crosses below your floor' });
    }
  }
  if (row.buyboxPriceCents !== null) {
    const wasAtOrBelowBuyBox = live === null || live <= row.buyboxPriceCents;
    if (wasAtOrBelowBuyBox && simulatedCents > row.buyboxPriceCents) {
      crossings.push({ key: 'buybox', label: 'crosses above the buy box' });
    }
  }
  if (row.lowestCompetitorCents !== null) {
    const wasAtOrAboveLowest = live === null || live >= row.lowestCompetitorCents;
    if (wasAtOrAboveLowest && simulatedCents < row.lowestCompetitorCents) {
      crossings.push({ key: 'lowestCompetitor', label: 'passes lowest competitor' });
    }
  }
  return crossings;
}

export interface SimulatedRow {
  priceCents: number;
  feesCents: number | null;
  marginCents: number | null;
  marginFrac: number | null;
  /** null when there's no cost basis to verdict against (mirrors the row's
   *  own 'no-cost-basis' case — the simulator can't invent a verdict a real
   *  re-price wouldn't have either). */
  verdictTier: 'underwater' | 'below-floor' | 'below-target' | 'optimal' | null;
  perUnitProfitCents: number | null;
  sellThroughProfitCents: number | null;
  crossings: PriceCrossing[];
}

/** The full recompute for one row at a simulated price — every number the
 *  stepper UI shows, in one call. */
export function simulateRowAtPrice(
  row: PriceCalibrationRow,
  priceCents: number,
  thresholds: { minFrac: number; targetFrac: number } | null
): SimulatedRow {
  const feesCents = feesAtPriceCents(row, priceCents);
  const marginCents = sharedUnitProfitCents(priceCents, feesCents, row.costCents);
  let marginFrac: number | null = null;
  let verdictTier: SimulatedRow['verdictTier'] = null;

  if (marginCents !== null) {
    marginFrac = priceCents > 0 ? marginCents / priceCents : null;
    if (thresholds && marginFrac !== null) {
      verdictTier = verdictTierForMargin(marginCents, marginFrac, thresholds);
    }
  }

  return {
    priceCents,
    feesCents,
    marginCents,
    marginFrac,
    verdictTier,
    perUnitProfitCents: marginCents,
    sellThroughProfitCents: marginCents !== null ? row.unitsOnHand * marginCents : null,
    crossings: priceCrossings(row, priceCents),
  };
}

/** Delta between the simulated row's projected sell-through profit and the
 *  LIVE row's — the "simulation: this row at $21.49 would add +$84
 *  projected profit" line under the projection strip. null when either side
 *  has no margin to compare (nothing to project). */
export function simulationProjectedDeltaCents(
  row: Pick<PriceCalibrationRow, 'unitsOnHand' | 'currentMarginCents'>,
  simulated: SimulatedRow
): number | null {
  if (simulated.sellThroughProfitCents === null || row.currentMarginCents === null) return null;
  const liveSellThroughProfitCents = row.unitsOnHand * row.currentMarginCents;
  return simulated.sellThroughProfitCents - liveSellThroughProfitCents;
}

// ─────────────────────────────────────────────────────────
// Live multi-SKU scenario map (velocity + scenario plan Task 3,
// 2026-08-26/31 — docs/plans/2026-08-26-price-calibration-velocity-
// scenario-plan.md). Replaces the single-row `Simulation` above: several
// SKUs can carry an independent "try a price" at once, keyed by seller SKU,
// never persisted or written to Amazon. Every helper here is a thin
// PriceCalibrationRow adapter over profit-scenario.ts's page-neutral
// kernel — the kernel owns the math; this module owns which row fields
// feed it and how the result reads on screen.
// ─────────────────────────────────────────────────────────

export const DEFAULT_INVENTORY_SCOPE: InventoryScope = 'sellable-now';

export const INVENTORY_SCOPE_OPTIONS: { key: InventoryScope; label: string }[] = [
  { key: 'sellable-now', label: 'Sellable now' },
  { key: 'all-owned', label: 'All owned' },
];

/** Row -> kernel InventoryBuckets. `unitsOnHand` is NOT used here — the web
 *  moves to these explicit scoped buckets per Task 2's design (unitsOnHand
 *  stays only for deploy skew, in the older simulator above). */
export function rowInventoryBuckets(
  row: Pick<PriceCalibrationRow, 'sellableNowUnits' | 'allOwnedUnits'>
): InventoryBuckets {
  return { sellableNow: row.sellableNowUnits, allOwned: row.allOwnedUnits };
}

export function selectedUnitsForRow(
  row: Pick<PriceCalibrationRow, 'sellableNowUnits' | 'allOwnedUnits'>,
  scope: InventoryScope
): number {
  return selectedUnitsForScope(rowInventoryBuckets(row), scope);
}

/** Zero selected units in the ACTIVE scope — SCOPE-specific, not a static
 *  per-row flag (a row can be zero-stock sellable-now but nonzero all-owned,
 *  or vice versa). Drives the default-hidden row visibility below; unrelated
 *  to whether the row has a current price (a stocked, unpriced row is never
 *  "out of stock"). */
export function isOutOfStockInScope(
  row: Pick<PriceCalibrationRow, 'sellableNowUnits' | 'allOwnedUnits'>,
  scope: InventoryScope
): boolean {
  return selectedUnitsForRow(row, scope) <= 0;
}

/** Founder finding 4 (2026-08-30 UAT): "we should hide the products which
 *  are not in stock, because if they are not in stock what's the point of
 *  price calibration." Zero-stock-in-scope rows never appear in the default
 *  list (any filter other than 'out-of-stock') and appear ONLY under the
 *  explicit 'out-of-stock' pill, which then shows nothing BUT those rows
 *  (verdict filtering doesn't apply — an out-of-stock row's verdict isn't
 *  what the operator is asking to see). A row with stock but no current
 *  price is a real pricing decision, not an out-of-stock exclusion, and
 *  stays visible in the default view like any other row. */
export function visiblePriceCalibrationRows(
  rows: PriceCalibrationRow[],
  scope: InventoryScope,
  filter: PriceCalibrationFilterKey
): PriceCalibrationRow[] {
  if (filter === 'out-of-stock') {
    return rows.filter((r) => isOutOfStockInScope(r, scope));
  }
  return filterPriceCalibrationRows(
    rows.filter((r) => !isOutOfStockInScope(r, scope)),
    filter
  );
}

/** Row -> kernel ScenarioRowInput. Cost is the canonical landed Amazon
 *  listing-unit cost (`landedUnitCostCents`) — NEVER the bare invoice
 *  `costCents` the older simulator above uses, per the design doc's
 *  "a bare supplier-unit invoice cost is not a valid fallback" rule.
 *  Velocity is null unless the API marked it KNOWN — an absent/insufficient
 *  sales window is never substituted with the replenishment proxy. */
export function scenarioRowInputForRow(
  row: Pick<
    PriceCalibrationRow,
    | 'sellerSku'
    | 'sellableNowUnits'
    | 'allOwnedUnits'
    | 'landedUnitCostCents'
    | 'referralPct'
    | 'fixedFeeCents'
    | 'velocityKnown'
    | 'recentUnitsPerDay'
  >,
  scope: InventoryScope
): ScenarioRowInput {
  return {
    key: row.sellerSku,
    selectedUnits: selectedUnitsForRow(row, scope),
    costCents: row.landedUnitCostCents,
    referralPct: row.referralPct,
    fixedFeeCents: row.fixedFeeCents,
    recentUnitsPerDay: row.velocityKnown ? row.recentUnitsPerDay : null,
  };
}

/** This row's active "try a price," or its live current price when the
 *  scenario map has no entry for it — the map only ever holds DEVIATIONS
 *  from the live price. Null when there's no live price to fall back to
 *  either (nothing to try a price against yet). */
export function proposedPriceForRow(
  row: Pick<PriceCalibrationRow, 'sellerSku' | 'yourPriceCents'>,
  scenarios: ReadonlyMap<string, number>
): number | null {
  const override = scenarios.get(row.sellerSku);
  return override !== undefined ? override : row.yourPriceCents;
}

/** One row's current-vs-proposed scenario economics. When the row has no
 *  live price to anchor against, this returns null economics rather than
 *  inventing a $0 "current" price — a $0 anchor would compute a real (and
 *  wrong) fee/profit number instead of correctly reading as "unpriced."
 *  `selectedUnits` is always exact regardless — only the financials are
 *  withheld when data is missing. */
export function rowScenarioResult(
  row: PriceCalibrationRow,
  scope: InventoryScope,
  scenarios: ReadonlyMap<string, number>
): RowScenarioResult {
  const selectedUnits = selectedUnitsForRow(row, scope);
  const proposedPriceCents = proposedPriceForRow(row, scenarios);

  if (row.yourPriceCents === null || proposedPriceCents === null) {
    const unpriced: RowPriceEconomics = {
      priceCents: 0,
      feesCents: null,
      unitProfitCents: null,
      remainingInventoryRevenueCents: null,
      remainingInventoryProfitCents: null,
      thirtyDayUnits: null,
      thirtyDayProfitCents: null,
    };
    return {
      key: row.sellerSku,
      selectedUnits,
      current: unpriced,
      proposed: unpriced,
      sellThroughProfitDeltaCents: null,
    };
  }

  return computeRowScenario(
    scenarioRowInputForRow(row, scope),
    row.yourPriceCents,
    proposedPriceCents
  );
}

/** "+$5.00 at the same sales rate" / "-$5.00 at the same sales rate" — the
 *  design doc's rule that every scenario delta names its constant-velocity
 *  assumption, never presented as a demand forecast. A dash (not "$0.00")
 *  when the delta is unknown — missing data is not a zero change. */
export function scenarioDeltaLabel(deltaCents: number | null): string {
  if (deltaCents === null) return '—';
  return `${signedMoney(deltaCents)} at the same sales rate`;
}

// ─────────────────────────────────────────────────────────
// Honest duration copy — "18 days" / "about 3 months" / an explicit unknown
// sentence. Zero/unknown velocity is NEVER rendered as "0 days" or an
// infinite/undefined duration.
// ─────────────────────────────────────────────────────────

export const SELL_THROUGH_UNKNOWN_LABEL = 'No recent sales — sell-through time unknown';

/** Plain-language duration for an exact day count. Under 60 days reads in
 *  days; 60 and beyond switches to whole months (rounded) — the plan's own
 *  examples ("18 days", "about 3 months") land on either side of that
 *  boundary. This is display formatting only; callers decide whether a day
 *  count exists at all (see sellThroughDurationLabel / blendedRunway). */
export function humanDurationLabel(days: number): string {
  const roundedDays = Math.max(0, Math.round(days));
  if (roundedDays < 60) {
    return `${roundedDays} day${roundedDays === 1 ? '' : 's'}`;
  }
  const months = Math.round(roundedDays / 30);
  return `about ${months} month${months === 1 ? '' : 's'}`;
}

/** selected units ÷ recent units/day, straight-lined at the recent rate.
 *  Null (not Infinity, not zero) whenever velocity is unknown OR is a
 *  known zero — a zero sales rate would otherwise divide out to an
 *  infinite/undefined sell-through time, which is exactly the "infinite
 *  profit" misread the design doc forbids. */
export function estimatedSellThroughDays(
  selectedUnits: number,
  recentUnitsPerDay: number | null
): number | null {
  if (recentUnitsPerDay === null || recentUnitsPerDay <= 0) return null;
  if (selectedUnits <= 0) return 0;
  return selectedUnits / recentUnitsPerDay;
}

/** The row-level duration line: a human duration, or the explicit unknown
 *  sentence for zero/unknown velocity. */
export function sellThroughDurationLabel(
  selectedUnits: number,
  recentUnitsPerDay: number | null
): string {
  const days = estimatedSellThroughDays(selectedUnits, recentUnitsPerDay);
  return days === null ? SELL_THROUGH_UNKNOWN_LABEL : humanDurationLabel(days);
}

/** Beyond 90 estimated days at a KNOWN velocity — the design doc's
 *  portfolio-level ">90 days" warning count. Unknown velocity is its own
 *  separate bucket (see velocityCoverage), never folded into this one. */
export function isSlowMoving(selectedUnits: number, recentUnitsPerDay: number | null): boolean {
  const days = estimatedSellThroughDays(selectedUnits, recentUnitsPerDay);
  return days !== null && days > 90;
}

export interface VelocityCoverage {
  slowMovingRowCount: number;
  slowMovingUnits: number;
  unknownVelocityRowCount: number;
  unknownVelocityUnits: number;
}

/** Tallies the two top-level warning counts from the design doc: SKUs
 *  projected beyond 90 days at a known rate, and SKUs with no usable
 *  velocity at all. Zero-stock rows contribute to neither — they carry no
 *  sell-through claim in the first place. */
export function velocityCoverage(
  rows: { selectedUnits: number; recentUnitsPerDay: number | null }[]
): VelocityCoverage {
  const coverage: VelocityCoverage = {
    slowMovingRowCount: 0,
    slowMovingUnits: 0,
    unknownVelocityRowCount: 0,
    unknownVelocityUnits: 0,
  };

  for (const r of rows) {
    if (r.selectedUnits <= 0) continue;
    if (r.recentUnitsPerDay === null) {
      coverage.unknownVelocityRowCount += 1;
      coverage.unknownVelocityUnits += r.selectedUnits;
      continue;
    }
    if (isSlowMoving(r.selectedUnits, r.recentUnitsPerDay)) {
      coverage.slowMovingRowCount += 1;
      coverage.slowMovingUnits += r.selectedUnits;
    }
  }

  return coverage;
}

export const BLENDED_RUNWAY_UNKNOWN_LABEL = 'unknown — no SKUs with known recent sales velocity';

export interface BlendedRunway {
  /** total selected units ÷ total known units/day, across rows with a
   *  KNOWN velocity only. Null when no row in scope has one. */
  days: number | null;
  durationLabel: string;
  /** Fixed caption per the design doc: this is a blended INVENTORY RUNWAY
   *  (a straight-line estimate at the recent rate), explicitly NOT the
   *  date every SKU sells out. */
  qualifier: string;
}

/** Portfolio-level runway: sums units and units/day only across rows with
 *  a KNOWN positive velocity and nonzero selected units — an unknown-
 *  velocity SKU's units are excluded rather than silently treated as
 *  moving at zero speed (which would only ever drag the blend down, never
 *  correctly say "we don't know"). */
export function blendedRunway(
  rows: { selectedUnits: number; recentUnitsPerDay: number | null }[]
): BlendedRunway {
  let totalUnits = 0;
  let totalUnitsPerDay = 0;
  let anyKnown = false;

  for (const r of rows) {
    if (r.selectedUnits <= 0) continue;
    if (r.recentUnitsPerDay === null || r.recentUnitsPerDay <= 0) continue;
    totalUnits += r.selectedUnits;
    totalUnitsPerDay += r.recentUnitsPerDay;
    anyKnown = true;
  }

  const days = anyKnown && totalUnitsPerDay > 0 ? totalUnits / totalUnitsPerDay : null;

  return {
    days,
    durationLabel: days === null ? BLENDED_RUNWAY_UNKNOWN_LABEL : humanDurationLabel(days),
    qualifier:
      'A straight-line estimate at the recent sales rate, not the date every SKU sells out.',
  };
}

// ─────────────────────────────────────────────────────────
// The full live summary the page's top-level cards render: exact selected
// units, the multi-SKU financial/30-day aggregate, blended runway, and
// velocity coverage warnings — all recomputed synchronously from the
// current scope + scenario map on every price edit.
// ─────────────────────────────────────────────────────────

export interface PriceCalibrationScenarioSummary {
  scope: InventoryScope;
  /** Exact selected units across every row in scope, including zero-stock
   *  rows (which simply contribute 0). */
  selectedUnits: number;
  aggregate: ScenarioAggregate;
  blendedRunway: BlendedRunway;
  velocityCoverage: VelocityCoverage;
}

/**
 * Deploy-skew guard (new web + old API). An API build that predates Task 2
 * omits sellableNowUnits/allOwnedUnits/velocity from every row entirely; the
 * schema's `.default(0)`/`.default(false)` guards keep parsing from
 * crashing, but a defaulted 0 is then indistinguishable from a real
 * zero-stock row — every row would read "0 selected units" and the
 * scenario totals would silently zero out, which is exactly the
 * "missing data coerced to zero" failure the design doc forbids. Returns a
 * user-facing note when the response's `inventoryContextAvailable` flag
 * says this API build never ran the context read; null once it has. */
export function inventoryContextUnavailableNote(
  response: Pick<PriceCalibrationResponse, 'inventoryContextAvailable'>
): string | null {
  if (response.inventoryContextAvailable) return null;
  return 'Inventory scope, sell-through velocity, and scenario totals are not available yet — this server is still updating. Prices and margins above are unaffected.';
}

export function buildScenarioSummary(
  rows: PriceCalibrationRow[],
  scope: InventoryScope,
  scenarios: ReadonlyMap<string, number>
): PriceCalibrationScenarioSummary {
  const results = rows.map((r) => rowScenarioResult(r, scope, scenarios));
  const velocityRows = rows.map((r) => ({
    selectedUnits: selectedUnitsForRow(r, scope),
    recentUnitsPerDay: r.velocityKnown ? r.recentUnitsPerDay : null,
  }));
  const selectedUnits = velocityRows.reduce((sum, r) => sum + Math.max(0, r.selectedUnits), 0);

  return {
    scope,
    selectedUnits,
    aggregate: aggregateScenarioResults(results),
    blendedRunway: blendedRunway(velocityRows),
    velocityCoverage: velocityCoverage(velocityRows),
  };
}

// ─────────────────────────────────────────────────────────
// Competitor stock and clearance copy (2026-10-05) — plain-English lines for
// the row's market line, Clearance block and competitor table, and the
// toolbar's "checked" text. Pure; `now` is passed in so tests are exact.
// ─────────────────────────────────────────────────────────

/** "just now", "45 min ago", "2 h ago", "3 d ago". A timestamp in the
 *  future (clock skew) reads as "just now". */
export function agoLabel(iso: string, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} h ago`;
  return `${Math.floor(minutes / (24 * 60))} d ago`;
}

const MONTH_ABBREVIATIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** "Oct 4". UTC fields, like shortDateLabel, so it never depends on the
 *  viewer's timezone. */
function monthDayLabel(iso: string): string {
  const d = new Date(iso);
  return `${MONTH_ABBREVIATIONS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** One line for the Clearance block's headline. Exit and fix-listing carry
 *  no price. A hold with no price (the API always sends one) falls back to
 *  plain words rather than "Hold at —". */
export function clearanceHeadline(
  clearance: Pick<PriceCalibrationClearance, 'action' | 'priceCents'>
): string {
  const price = money(clearance.priceCents);
  switch (clearance.action) {
    case 'match_tier':
      return `Set ${price} to match the market`;
    case 'hold':
      return clearance.priceCents === null ? 'Hold the current price' : `Hold at ${price}`;
    case 'wait_for_amazon':
      return `Set ${price} and wait for Amazon to run out`;
    case 'undercut_amazon':
      return `Set ${price}, 3% under Amazon`;
    case 'restore_buy_box':
      return `Step down to ${price} to bring the Buy Box back`;
    case 'exit':
      return 'Exit: no price returns cash';
    case 'fix_listing':
      return 'Fix the listing first';
  }
}

/** "Pays $4.18 a unit after fees. $790 back on 189 units; they cost $718."
 *  `units` is the count the engine used for cashBackCents (sellable units).
 *  Null when there is no payout (exit, fix the listing). A negative payout
 *  says "Loses" instead of printing a minus sign. */
export function clearanceMoneyLine(
  clearance: Pick<PriceCalibrationClearance, 'payoutCents' | 'cashBackCents' | 'costAtStakeCents'>,
  units: number
): string | null {
  const { payoutCents, cashBackCents, costAtStakeCents } = clearance;
  if (payoutCents === null) return null;

  const loses = payoutCents < 0;
  let line = loses
    ? `Loses ${money(-payoutCents)} a unit after fees.`
    : `Pays ${money(payoutCents)} a unit after fees.`;
  if (cashBackCents !== null) {
    const unitWord = `${units.toLocaleString('en-US')} unit${units === 1 ? '' : 's'}`;
    line += loses
      ? ` ${moneyWhole(-cashBackCents)} lost on ${unitWord}`
      : ` ${moneyWhole(cashBackCents)} back on ${unitWord}`;
    line +=
      costAtStakeCents !== null
        ? `; ${units === 1 ? 'it costs' : 'they cost'} ${moneyWhole(costAtStakeCents)}.`
        : '.';
  }
  return line;
}

/** "12 sellers below us hold 247 FBA units (260 on Oct 4). Checked 2 h ago."
 *  The bracket shows the same figure from the snapshot before, when there is
 *  one. With nobody below us: "No seller is priced below us. Checked ..." */
export function competitionSummaryLine(
  competition: Pick<
    PriceCalibrationCompetition,
    | 'capturedAt'
    | 'cheaperSellerCount'
    | 'cheaperFbaUnits'
    | 'previousCheaperFbaUnits'
    | 'previousCapturedAt'
  >,
  now: Date
): string {
  const checked = `Checked ${agoLabel(competition.capturedAt, now)}.`;
  const sellers = competition.cheaperSellerCount;
  if (sellers === 0) return `No seller is priced below us. ${checked}`;

  const units = competition.cheaperFbaUnits;
  const earlier =
    competition.previousCheaperFbaUnits !== null && competition.previousCapturedAt !== null
      ? ` (${competition.previousCheaperFbaUnits.toLocaleString('en-US')} on ${monthDayLabel(competition.previousCapturedAt)})`
      : '';
  return `${sellers.toLocaleString('en-US')} seller${sellers === 1 ? '' : 's'} below us ${
    sellers === 1 ? 'holds' : 'hold'
  } ${units.toLocaleString('en-US')} FBA unit${units === 1 ? '' : 's'}${earlier}. ${checked}`;
}

/** Age of one offer's Keepa stock reading, for the competitor table.
 *  Keepa re-reads the cheapest offers on each refresh, so a reading under
 *  three hours old is "today". */
export function stockReadingLabel(stockAgeMinutes: number | null): string {
  if (stockAgeMinutes === null) return 'not read';
  if (stockAgeMinutes < 180) return 'today';
  if (stockAgeMinutes < 24 * 60) return `${Math.floor(stockAgeMinutes / 60)} h ago`;
  return `${Math.floor(stockAgeMinutes / (24 * 60))} d ago`;
}

/** Newest competitor snapshot time across rows (ISO), or null when no row
 *  has one. */
export function latestCompetitionCapturedAt(
  rows: Array<Pick<PriceCalibrationRow, 'competition'>>
): string | null {
  let latest: string | null = null;
  for (const r of rows) {
    const at = r.competition?.capturedAt;
    if (at && (latest === null || new Date(at).getTime() > new Date(latest).getTime())) latest = at;
  }
  return latest;
}

// ─────────────────────────────────────────────────────────
// Seller Central prices — Seller Central takes three numbers per SKU: the
// price, a minimum price and a maximum price. Sourcito never writes to Amazon
// (CLAUDE.md hard rule 5), so these are figures to type in, not an update.
// ─────────────────────────────────────────────────────────

export interface SellerCentralPrices {
  /** What to set now. */
  priceCents: number;
  minCents: number | null;
  maxCents: number | null;
  priceBasis: 'simulated' | 'clearance' | 'break-even' | 'min-margin' | 'current';
  minBasis: 'fee-floor' | 'break-even' | 'min-margin' | 'price' | null;
  maxBasis: 'target-margin' | 'price' | null;
}

/** The price, minimum and maximum to enter in Seller Central for one row.
 *  Price: the tried price, else the clearance price, else the verdict's fix
 *  (break-even or minimum margin), else the live price. Min is the fee floor,
 *  else break-even, else the minimum-margin price, and never above Price.
 *  Max is the target-margin price, and never below Price. Null only when the
 *  row has no price to set. */
export function sellerCentralPrices(
  row: PriceCalibrationRow,
  opts: {
    proposedPriceCents: number | null;
    isSimulating: boolean;
    breakEvenCents: number | null;
    lowestNoLossCents: number | null;
  }
): SellerCentralPrices | null {
  let priceCents: number | null;
  let priceBasis: SellerCentralPrices['priceBasis'];
  if (opts.isSimulating && opts.proposedPriceCents !== null) {
    priceCents = opts.proposedPriceCents;
    priceBasis = 'simulated';
  } else if (row.clearance?.priceCents != null) {
    priceCents = row.clearance.priceCents;
    priceBasis = 'clearance';
  } else if (row.verdict === 'underwater' && opts.breakEvenCents !== null) {
    priceCents = opts.breakEvenCents;
    priceBasis = 'break-even';
  } else if (row.verdict === 'below-floor' && row.minPriceCents !== null) {
    priceCents = row.minPriceCents;
    priceBasis = 'min-margin';
  } else {
    priceCents = row.yourPriceCents;
    priceBasis = 'current';
  }
  if (priceCents === null) return null;

  let minCents: number | null;
  let minBasis: SellerCentralPrices['minBasis'];
  if (row.clearance?.floorCents != null) {
    minCents = row.clearance.floorCents;
    minBasis = 'fee-floor';
  } else if (opts.lowestNoLossCents !== null) {
    minCents = opts.lowestNoLossCents;
    minBasis = 'break-even';
  } else if (row.minPriceCents !== null) {
    minCents = row.minPriceCents;
    minBasis = 'min-margin';
  } else {
    minCents = null;
    minBasis = null;
  }
  if (minCents !== null && minCents > priceCents) {
    minCents = priceCents;
    minBasis = 'price';
  }

  let maxCents = priceCents;
  let maxBasis: SellerCentralPrices['maxBasis'] = 'price';
  if (row.targetPriceCents !== null && row.targetPriceCents >= priceCents) {
    maxCents = row.targetPriceCents;
    maxBasis = 'target-margin';
  }

  return { priceCents, minCents, maxCents, priceBasis, minBasis, maxBasis };
}

const SELLER_CENTRAL_MIN_BASIS_LINES: Record<
  NonNullable<SellerCentralPrices['minBasis']>,
  string
> = {
  'fee-floor': 'Min is the fee floor.',
  'break-even': 'Min is break-even.',
  'min-margin': 'Min is your minimum-margin price.',
  price: 'Min is the price itself.',
};

const SELLER_CENTRAL_MAX_BASIS_LINES: Record<
  NonNullable<SellerCentralPrices['maxBasis']>,
  string
> = {
  'target-margin': 'Max is your target-margin price.',
  price: 'Max is the price itself.',
};

/** "Min is the fee floor. Max is your target-margin price." Says where the
 *  min and max came from, so the numbers are not a black box. */
export function sellerCentralBasisLine(prices: SellerCentralPrices): string {
  const min =
    prices.minBasis === null
      ? 'Min is not available.'
      : SELLER_CENTRAL_MIN_BASIS_LINES[prices.minBasis];
  const max =
    prices.maxBasis === null
      ? 'Max is not available.'
      : SELLER_CENTRAL_MAX_BASIS_LINES[prices.maxBasis];
  return `${min} ${max}`;
}

/** "sku<TAB>price<TAB>min<TAB>max" as plain dollar amounts with no $ sign;
 *  an unknown min or max is left empty. Pastes into a spreadsheet as four
 *  cells. */
export function sellerCentralCopyLine(sku: string, prices: SellerCentralPrices): string {
  const dollars = (cents: number | null) => (cents === null ? '' : (cents / 100).toFixed(2));
  return [sku, dollars(prices.priceCents), dollars(prices.minCents), dollars(prices.maxCents)].join(
    '\t'
  );
}
